import React, { useEffect, useRef } from 'react';
import { gsap, ScrollTrigger, prefersReducedMotion } from '../../utils/gsap';

/**
 * HeroChapter — 300vh Pinned Spatial Parallax Scene
 *
 * The viewport is pinned. Scroll drives the scene:
 * 0–25%:  Enormous "SHAWAIZ" centered. Background grid drifts at 0.3x.
 * 25–50%: Name scales down to headline. Engineer identity slides in from right.
 *          A thin vertical line draws downward from the name.
 * 50–75%: Background annotation text drifts left at 0.4x while foreground stays.
 *          "I've always liked building things" reveals via clip-path from below.
 * 75–100%: Hero exits LEFT while the story begins underneath.
 */
const HeroChapter = () => {
  const wrapperRef = useRef(null);
  const sceneRef = useRef(null);
  const gridRef = useRef(null);
  const bigNameRef = useRef(null);
  const surnameRef = useRef(null);
  const nameGroupRef = useRef(null);
  const identityRef = useRef(null);
  const statementRef = useRef(null);
  const scrollPromptRef = useRef(null);
  const bgAnnotationRef = useRef(null);

  // Flying Paper Airplane & Trail refs for transition gap
  const planeRef = useRef(null);
  const trailRef = useRef(null);

  useEffect(() => {
    if (prefersReducedMotion()) return;

    let ctx;
    const init = () => {
      ctx = gsap.context(() => {
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: wrapperRef.current,
            start: 'top top',
            end: '+=160%',
            pin: sceneRef.current,
            scrub: 1,
            anticipatePin: 1,
          }
        });

        // === 0–25% === Grid drifts, main name group gracefully scales down and shifts up
        tl.to(gridRef.current, { x: '-8%', ease: 'none', duration: 1 }, 0)
          .to(nameGroupRef.current, {
            scale: 0.78,
            y: '-14vh',
            ease: 'power2.inOut',
            duration: 1
          }, 0.5)

          // === 25–60% === bg annotation drifts, statement reveals cleanly directly below
          .to(bgAnnotationRef.current, { x: '-15vw', ease: 'none', duration: 1 }, 1.5)
          .fromTo(statementRef.current,
            { clipPath: 'inset(100% 0% 0% 0%)', y: 30, opacity: 0 },
            { clipPath: 'inset(0% 0% 0% 0%)', y: 0, opacity: 1, ease: 'power4.out', duration: 1 },
            1.2
          )
          .to(scrollPromptRef.current, { opacity: 0, duration: 0.4 }, 1.2)

          // === 75–100% === Paper Airplane clean flight locked 100% to the SVG trail line
          const flightObj = { progress: 0 };

          // Set initial strokeDasharray and strokeDashoffset on trail line
          if (trailRef.current) {
            const pathLen = trailRef.current.getTotalLength() || 2200;
            gsap.set(trailRef.current, {
              strokeDasharray: pathLen,
              strokeDashoffset: pathLen
            });
          }

          tl.to(flightObj, {
            progress: 1,
            ease: 'sine.inOut',
            duration: 1.6,
            onUpdate: () => {
              if (!trailRef.current || !planeRef.current) return;
              const path = trailRef.current;
              const totalLen = path.getTotalLength() || 2200;
              const currentLen = flightObj.progress * totalLen;

              // 1. Draw trail line synchronously
              gsap.set(path, { strokeDashoffset: totalLen - currentLen });

              // 2. Calculate exact coordinate of leading tip
              const pt = path.getPointAtLength(currentLen);
              const ptNext = path.getPointAtLength(Math.min(totalLen, currentLen + 4));
              const angle = Math.atan2(ptNext.y - pt.y, ptNext.x - pt.x) * (180 / Math.PI);

              // 3. Convert SVG viewBox coordinates (1800 x 600) to viewport percentages
              const leftPercent = (pt.x / 1800) * 100;
              const topPercent = (pt.y / 600) * 100;

              // 4. Lock plane position directly to trail tip
              gsap.set(planeRef.current, {
                left: `${leftPercent}%`,
                top: `${topPercent}%`,
                rotation: angle + 22,
                xPercent: -50,
                yPercent: -50,
                opacity: flightObj.progress > 0.01 ? 1 : 0
              });
            }
          }, 2.6)
          // Seamless exit transition into Chapter 1 (No blank screen gap)
          .to(sceneRef.current, {
            opacity: 0,
            ease: 'power2.in',
            duration: 0.6
          }, 3.6);
      });
    };

    // Ensure DOM is ready
    ScrollTrigger.refresh();
    init();

    return () => {
      if (ctx) ctx.revert();
    };
  }, []);

  return (
    <section id="section-hero" ref={wrapperRef} className="hero-scroll-wrapper">
      {/* The pinned scene */}
      <div ref={sceneRef} className="hero-scene">

        {/* Flying Paper Airplane & Flight Trail (Visible during transition) */}
        <div className="hero-airplane-stage" aria-hidden="true">
          <svg viewBox="0 0 1800 600" width="100%" height="100%" preserveAspectRatio="none" className="hero-airplane-svg">
            <path
              ref={trailRef}
              d="M -100 500 C 400 550, 850 160, 2200 180"
              fill="none"
              stroke="rgba(212,163,89,0.55)"
              strokeWidth="2.5"
              strokeDasharray="2200"
            />
          </svg>

          <div ref={planeRef} className="hero-paper-plane">
            <svg viewBox="0 0 64 64" width="48" height="48" fill="none">
              <path
                d="M 2 32 L 60 4 L 40 60 L 28 38 Z"
                fill="url(#plane-grad)"
                stroke="#d4a359"
                strokeWidth="1.5"
              />
              <path
                d="M 60 4 L 28 38 L 18 48 L 22 36 Z"
                fill="rgba(212, 163, 89, 0.45)"
                stroke="#d4a359"
                strokeWidth="1"
              />
              <defs>
                <linearGradient id="plane-grad" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#ffffff" />
                  <stop offset="100%" stopColor="#d4a359" />
                </linearGradient>
              </defs>
            </svg>
          </div>
        </div>

        {/* Background layer — drifts at slower speed */}
        <div ref={gridRef} className="hero-bg-layer" aria-hidden="true">
          <div className="hero-grid-lines" />
          <span ref={bgAnnotationRef} className="hero-bg-annotation font-mono">
            MOBILE&nbsp;&nbsp;/&nbsp;&nbsp;SYSTEMS&nbsp;&nbsp;/&nbsp;&nbsp;SOFTWARE&nbsp;&nbsp;/&nbsp;&nbsp;ENGINEERING&nbsp;&nbsp;/&nbsp;&nbsp;LAHORE
          </span>
        </div>

        {/* Main name & identity group — centered together */}
        <div ref={nameGroupRef} className="hero-name-group">
          <h1 ref={bigNameRef} className="hero-name-first font-display">
            SHAWAIZ
          </h1>
          <div ref={surnameRef} className="hero-name-second font-display">
            ALI REHMAN
          </div>
          <div ref={identityRef} className="hero-identity font-mono">
            <div className="hero-identity-line" />
            <span>MOBILE &amp; SOFTWARE ENGINEER</span>
            <div className="hero-identity-line" />
          </div>
        </div>

        {/* Scroll-revealed statement cleanly centered below */}
        <div ref={statementRef} className="hero-statement font-display">
          I've always liked<br />
          <em>building things.</em>
        </div>

        {/* Scroll prompt */}
        <div ref={scrollPromptRef} className="hero-scroll-cue font-mono">
          <div className="scroll-cue-line" />
          <span>SCROLL</span>
        </div>
      </div>

      <style>{`
        .hero-scroll-wrapper {
          position: relative;
          height: 260vh; /* scroll distance for the scene */
          background: #080a0f;
          overflow: hidden;
        }

        .hero-scene {
          position: relative;
          width: 100%;
          height: 100vh;
          overflow: visible;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #080a0f;
          will-change: transform;
        }

        /* Background layer */
        .hero-bg-layer {
          position: absolute;
          inset: 0;
          z-index: 1;
          pointer-events: none;
          will-change: transform;
        }

        .hero-grid-lines {
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(212, 163, 89, 0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(212, 163, 89, 0.04) 1px, transparent 1px);
          background-size: 80px 80px;
        }

        .hero-bg-annotation {
          position: absolute;
          bottom: 12%;
          left: -5%;
          font-size: clamp(0.7rem, 1.1vw, 0.85rem);
          letter-spacing: 0.28em;
          color: rgba(212, 163, 89, 0.75);
          font-weight: 600;
          white-space: nowrap;
          will-change: transform;
        }

        /* Name group — foreground centered */
        .hero-name-group {
          position: absolute;
          z-index: 10;
          top: 40%;
          left: 50%;
          transform: translate(-50%, -50%);
          text-align: center;
          will-change: transform;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.2rem;
        }

        .hero-name-first {
          font-size: clamp(2.4rem, 7.5vw, 5.5rem);
          font-weight: 800;
          line-height: 1.0;
          letter-spacing: -0.03em;
          color: #f3f4f6;
          margin: 0;
          will-change: transform, opacity;
          transform-origin: center center;
        }

        .hero-name-second {
          font-size: clamp(1.3rem, 3.8vw, 3rem);
          font-weight: 700;
          line-height: 1.1;
          letter-spacing: -0.01em;
          color: rgba(212, 163, 89, 0.85);
          will-change: transform, opacity;
          transform-origin: center center;
        }

        /* Identity block centered under name */
        .hero-identity {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-top: 0.75rem;
        }

        .hero-identity-line {
          width: 36px;
          height: 1px;
          background: rgba(212, 163, 89, 0.5);
        }

        .hero-identity span {
          font-size: clamp(0.65rem, 0.9vw, 0.82rem);
          letter-spacing: 0.24em;
          color: var(--accent-gold);
          font-weight: 600;
          white-space: nowrap;
        }

        /* Paper Airplane Stage Overlay */
        .hero-airplane-stage {
          position: absolute;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          z-index: 50;
          pointer-events: none;
          overflow: visible;
        }

        .hero-airplane-svg {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
        }

        .hero-paper-plane {
          position: absolute;
          top: 0;
          left: 0;
          opacity: 0; /* Hidden initially */
          will-change: transform, opacity;
          filter: drop-shadow(0 0 16px rgba(212, 163, 89, 0.7));
        }

        /* Statement — centered below name group */
        .hero-statement {
          position: absolute;
          z-index: 12;
          top: 64%;
          left: 50%;
          transform: translateX(-50%);
          font-size: clamp(1.6rem, 3.5vw, 2.8rem);
          line-height: 1.25;
          color: #f3f4f6;
          text-align: center;
          will-change: clip-path, transform, opacity;
          white-space: nowrap;
        }

        .hero-statement em {
          font-style: italic;
          color: var(--accent-gold);
          font-family: 'Playfair Display', Georgia, serif;
        }

        /* Scroll cue */
        .hero-scroll-cue {
          position: absolute;
          z-index: 20;
          bottom: 4vh;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.65rem;
          letter-spacing: 0.22em;
          color: rgba(107, 114, 128, 0.7);
          will-change: opacity;
        }

        .scroll-cue-line {
          width: 1px;
          height: 36px;
          background: linear-gradient(to bottom, rgba(212, 163, 89, 0.5), transparent);
          animation: heroPulse 2s infinite ease-in-out;
        }

        @keyframes heroPulse {
          0%, 100% { opacity: 0.3; transform: scaleY(0.5); }
          50% { opacity: 1; transform: scaleY(1); }
        }

        @media (max-width: 768px) {
          /* Enable paper airplane flight on mobile */
          .hero-airplane-stage {
            display: block;
            width: 100vw;
            height: 100vh;
          }

          .hero-paper-plane svg {
            width: 34px;
            height: 34px;
          }

          /* Full-viewport centered name block */
          .hero-name-group {
            top: 36%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 92vw;
            text-align: center;
          }

          .hero-name-first {
            font-size: clamp(2.2rem, 11vw, 3.8rem);
            letter-spacing: -0.04em;
          }

          .hero-name-second {
            font-size: clamp(1.2rem, 6vw, 2.0rem);
            letter-spacing: -0.01em;
          }

          .hero-identity {
            margin-top: 0.65rem;
            gap: 0.5rem;
          }

          .hero-identity span {
            font-size: clamp(0.6rem, 2.5vw, 0.72rem);
            letter-spacing: 0.16em;
          }

          .hero-identity-line {
            width: 24px;
          }

          /* Statement moves up — sits just below name group */
          .hero-statement {
            top: 66%;
            font-size: clamp(1.2rem, 5vw, 1.7rem);
            white-space: normal;
            width: 88vw;
            text-align: center;
            line-height: 1.3;
          }

          /* Background annotation — simpler on mobile */
          .hero-bg-annotation {
            font-size: 0.55rem;
            letter-spacing: 0.14em;
            bottom: 6vh;
            left: 50%;
            top: auto;
            transform: translateX(-50%);
            white-space: nowrap;
            opacity: 0.4;
          }

          .hero-scroll-wrapper {
            height: 250vh;
          }

          .hero-scroll-cue {
            bottom: 3.5vh;
          }
        }
      `}</style>
    </section>
  );
};

export default HeroChapter;
